/**
 * Event identifier in routes, checkout session, and cart APIs.
 * Legacy numeric IDs and newer string codes (e.g. reference_no) from the backend.
 */
export type EventRouteKey = string | number;

/** Route `:id` param — never coerce with `Number()` (breaks alphanumeric reference codes). */
export function parseEventRouteParam(raw: string | null): EventRouteKey | null {
  if (raw == null) return null;
  const t = raw.trim();
  return t.length ? t : null;
}

/** Compare two event keys from cart, URL, or JSON (`5` vs 5 vs leading zeros). */
export function eventKeysEqual(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return false;
  return String(a).trim() === String(b).trim();
}

/** Normalize cart/API `event_id` / `eventId` for routing and POST bodies. */
export function coerceCartEventKey(raw: unknown): EventRouteKey | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw > 0 ? raw : null;
  }
  const s = String(raw).trim();
  return s.length ? s : null;
}

export function requireEventKey(raw: unknown): EventRouteKey {
  const k = coerceCartEventKey(raw);
  if (k == null) throw new Error('Missing event id');
  return k;
}

/** Show id, ticket type id, line ticket id — same normalization as event ids (string codes or legacy numbers). */
export const coerceShowOrTicketTypeKey = coerceCartEventKey;

export function requireShowOrTicketTypeKey(raw: unknown): EventRouteKey {
  const k = coerceShowOrTicketTypeKey(raw);
  if (k == null) throw new Error('Missing show or ticket type id');
  return k;
}

/** Ticket tier id for POST /cart `ticket_types[].id` — prefers API `ticket_types_id`. */
export function resolveTicketTypeId(ticket: Record<string, unknown> | null | undefined): EventRouteKey | null {
  if (!ticket) return null;
  return coerceShowOrTicketTypeKey(
    ticket['ticket_types_id'] ?? ticket['ticketTypeId'] ?? ticket['ticket_type_id'] ?? ticket['id']
  );
}

/** Show slot id — prefers API `show_id`. */
export function resolveShowId(show: Record<string, unknown> | null | undefined): EventRouteKey | null {
  if (!show) return null;
  return coerceShowOrTicketTypeKey(show['show_id'] ?? show['id']);
}

/** Ensure `id` / `ticket_types_id` on each ticket and `id` / `show_id` on show after shows API. */
export function normalizeShowFromApi<T extends Record<string, unknown>>(show: T): T {
  const showId = resolveShowId(show);
  const rawTickets = show['tickets'];
  const tickets = Array.isArray(rawTickets)
    ? rawTickets.map((row) => {
        if (row == null || typeof row !== 'object' || Array.isArray(row)) return row;
        return normalizeTicketFromApi(row as Record<string, unknown>);
      })
    : rawTickets;
  return {
    ...show,
    ...(showId != null ? { id: showId, show_id: show['show_id'] ?? showId } : {}),
    tickets
  } as T;
}

export function normalizeTicketFromApi<T extends Record<string, unknown>>(ticket: T): T {
  const typeId = resolveTicketTypeId(ticket);
  if (typeId == null) return ticket;
  return {
    ...ticket,
    id: typeId,
    ticket_types_id: ticket['ticket_types_id'] ?? typeId
  } as T;
}
